// `avatar ai` — Quản lý AI provider config per-workspace (M12).
// 4 subcommand:
//   - setup  : Wizard re-config (reuse runAiSetupPhase từ phase 07)
//   - status : Show provider/model/baseUrl/token-mask hiện tại
//   - test   : Cheap prompt verify provider work
//   - reset  : Strip env.ANTHROPIC_* khỏi settings.json (về dùng default subscription)
//
// v1.3.1 fix: walk-up resolve Avatar workspace root (strict 3-marker check)
// thay vì chỉ check `.claude/` ở cwd. Tránh user chạy ở parent dir (vd
// /Users/Luke/Downloads/Programming/ có .claude/ của Claude Code generic)
// → ghi nhầm config vào sai chỗ.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { confirm } from "@inquirer/prompts";
import type { Command } from "commander";
import { assertInAvatarWorkspaceOrExit } from "../lib/assert-in-avatar-workspace-or-hint-foreign.js";
import { pathExists, readJson, writeJsonAtomic } from "../lib/filesystem-helpers.js";
import { maybePromptAiOrchestratorAfterAiSetup } from "../lib/maybe-prompt-ai-orchestrator-after-ai-setup.js";
import { runAiSetupPhase } from "../lib/run-ai-setup-phase.js";
import { maskApiKey } from "../lib/setup-llmlite-api-key-and-model.js";
import { log } from "../lib/terminal-logger.js";
import { testAiProviderByDetectedMode } from "../lib/test-ai-provider-by-detected-mode.js";
import {
  ALIAS_MODEL_OVERRIDE_KEYS,
  omitEnvKeys,
  PROVIDER_CREDENTIAL_KEYS,
} from "../lib/write-claude-settings-json-per-project.js";

// Resolve Avatar workspace root từ cwd (marker .claude/avatar.json). Exit 1 với
// hint đúng CLI nếu đứng nhầm nhà. Dùng guard chung — xem helper.
async function ensureWorkspaceCwd(): Promise<string> {
  return assertInAvatarWorkspaceOrExit(process.cwd());
}

// Read settings.json hiện có. Trả {} nếu chưa có file (subscription default state).
async function readWorkspaceSettings(workspacePath: string): Promise<Record<string, unknown>> {
  const settingsPath = join(workspacePath, ".claude", "settings.json");
  if (!(await pathExists(settingsPath))) return {};
  try {
    return await readJson<Record<string, unknown>>(settingsPath);
  } catch {
    return {};
  }
}

// === setup ===
async function runAiSetup(): Promise<void> {
  const workspacePath = await ensureWorkspaceCwd();
  const aiResult = await runAiSetupPhase({ workspacePath });
  // Sau re-setup key: hỏi ai-orchestrator giống init (chỉ khi non-subscription + >1
  // model). forceRefresh=true → nếu đã có map cũ, hỏi chọn lại (key có thể đã đổi model).
  try {
    await maybePromptAiOrchestratorAfterAiSetup(workspacePath, aiResult, { forceRefresh: true });
  } catch (err) {
    log.warn(`Setup AI Orchestrator fail: ${err instanceof Error ? err.message : err}`);
  }
}

// === status ===
// v1.6.0: Detect 4 provider modes — LLMLite (BASE_URL + AUTH_TOKEN), Anthropic Direct
// (BASE_URL + API_KEY), Custom (chỉ AUTH_TOKEN), Subscription (no env).
async function runAiStatus(): Promise<void> {
  const workspacePath = await ensureWorkspaceCwd();
  const settings = await readWorkspaceSettings(workspacePath);
  const env = (settings.env as Record<string, unknown> | undefined) || {};
  const baseUrl = typeof env.ANTHROPIC_BASE_URL === "string" ? env.ANTHROPIC_BASE_URL : undefined;
  const token = typeof env.ANTHROPIC_AUTH_TOKEN === "string" ? env.ANTHROPIC_AUTH_TOKEN : undefined;
  const apiKey = typeof env.ANTHROPIC_API_KEY === "string" ? env.ANTHROPIC_API_KEY : undefined;
  const model = typeof settings.model === "string" ? settings.model : undefined;

  // Distinguish Anthropic Direct (sk-ant- + api.anthropic.com) vs LLMLite (other gateway).
  let provider: string;
  let credentialDisplay: string;
  if (apiKey) {
    provider =
      baseUrl?.includes("api.anthropic.com") || apiKey.startsWith("sk-ant-")
        ? "Anthropic Direct"
        : "Custom (API key)";
    credentialDisplay = maskApiKey(apiKey);
  } else if (baseUrl && token) {
    provider = "LLMLite";
    credentialDisplay = maskApiKey(token);
  } else if (token) {
    provider = "Custom";
    credentialDisplay = maskApiKey(token);
  } else {
    provider = "Subscription (default)";
    credentialDisplay = "(không set — dùng subscription auth)";
  }

  log.info(`Project:  ${workspacePath}`);
  log.info(`Provider: ${provider}${baseUrl ? ` (${baseUrl})` : ""}`);
  log.info(`Model:    ${model ?? "(default — Claude Code chọn)"}`);
  log.info(`Token:    ${credentialDisplay}`);
}

// === test ===
// v1.2.3: Detect provider mode từ settings.json, test đúng path (LLMLite hoặc Subscription).
async function runAiTest(): Promise<void> {
  const workspacePath = await ensureWorkspaceCwd();
  const settings = await readWorkspaceSettings(workspacePath);
  try {
    const result = await testAiProviderByDetectedMode(settings);
    log.success(`✓ ${result.message}`);
  } catch (err) {
    log.error(`Test fail: ${(err as Error).message}`);
    process.exit(1);
  }
}

// === reset ===
async function runAiReset(opts: { yes?: boolean }): Promise<void> {
  const workspacePath = await ensureWorkspaceCwd();
  const settingsPath = join(workspacePath, ".claude", "settings.json");
  const settings = await readWorkspaceSettings(workspacePath);

  if (!opts.yes) {
    const ok = await confirm({
      message: "Xóa AI config (về dùng Claude Code Subscription default)?",
      default: false,
    });
    if (!ok) {
      log.dim("Đã hủy.");
      return;
    }
  }

  // Rebuild settings strip MỌI provider env keys (credential + baseUrl) VÀ alias-model
  // override (ANTHROPIC_DEFAULT_*_MODEL do ai-orchestrator ghi) — nếu để sót, alias
  // opus/sonnet/haiku vẫn map về model provider cũ dù đã reset về subscription.
  // v1.6.0: include ANTHROPIC_API_KEY (Anthropic direct provider key).
  const { env: existingEnv, ...rest } = settings as { env?: Record<string, unknown> };
  const reset: Record<string, unknown> = { ...rest };
  if (existingEnv) {
    const envRest = omitEnvKeys(existingEnv, [
      ...PROVIDER_CREDENTIAL_KEYS,
      ...ALIAS_MODEL_OVERRIDE_KEYS,
    ]);
    if (envRest && Object.keys(envRest).length > 0) {
      reset.env = envRest;
    }
  }

  // Nếu sau strip object rỗng → xóa file luôn (clean state).
  if (Object.keys(reset).length === 0) {
    await fs.unlink(settingsPath).catch(() => {});
    log.success("Đã xóa .claude/settings.json (clean state)");
  } else {
    await writeJsonAtomic(settingsPath, reset, 0o600);
    log.success("Đã reset env block trong .claude/settings.json");
  }
}

export function registerAiCommand(program: Command): void {
  const ai = program.command("ai").description("Quản lý AI provider config (M12)");

  ai.command("setup")
    .description("Wizard setup/re-config AI provider cho workspace hiện tại")
    .action(async () => {
      await runAiSetup();
    });

  ai.command("status")
    .description("Show AI config hiện tại (mask token)")
    .action(async () => {
      await runAiStatus();
    });

  ai.command("test")
    .description("Verify AI provider qua cheap prompt")
    .action(async () => {
      await runAiTest();
    });

  ai.command("reset")
    .description("Xóa env.ANTHROPIC_* khỏi settings.json (về Subscription default)")
    .option("--yes", "Skip confirm")
    .action(async (opts: { yes?: boolean }) => {
      await runAiReset(opts);
    });
}
